import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import pluralize from 'pluralize';
import { BiContainer, Button, CircularLoader, Column, IconButton, Switch, TableList, Tooltip, Typography, useDevice } from 'seagull';
import { Edit } from 'seagull/icons';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { NotificationSettingsEntryType, NotificationStatus } from '~/models/NotificationSettingsModel';
import { selectors } from '~/store/state-selectors';
import { deriveIsActive, formatClientReminderInterval, getNotificationsToShow, getRecipientTypesText, isDays, minutesToDays, notificationSettingsTextsPath, Tab, Table } from '../../NotificationSettings.utils';
import styles from './NotificationSettingsTableV2.module.scss';
import { GetNotificationSettingsTableV2TID } from './NotificationSettingsTableV2.testIds';
const typeMap = {
    [Table.Reminders]: NotificationSettingsEntryType.Recurring,
    [Table.Notifications]: NotificationSettingsEntryType.Single
};
// Status column fills remaining row width; min/max keep the Edit pill + toggle visible on narrow tables.
const STATUS_COLUMN_MIN_WIDTH = 200;
const STATUS_COLUMN_MAX_WIDTH = 800;
const formatDelay = (minutes)=>isDays(minutes) ? pluralize('day', minutesToDays(minutes), true) : pluralize('minute', minutes, true);
export function NotificationSettingsTableV2(param) {
    let { data, tableType, selectedTab, onEditClick, onToggleClick, pendingNotificationName, isLoading, testId = "".concat(tableType, "-table-v2") } = param;
    const { NotificationSettingsTableV2TestIds: testIds } = useMemo(()=>GetNotificationSettingsTableV2TID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys(notificationSettingsTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const featureFlags = useFeatureFlags();
    const { isMobileDevice } = useDevice();
    const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
    const showClientHubIntegration = useStore(selectors.integration.showClientHubIntegration);
    const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
    const showKeeperIntegration = useStore(selectors.integration.showKeeperIntegration);
    const notificationsTextMap = getNotificationsToShow({
        tab: selectedTab,
        showKarbonIntegration,
        showClientHubIntegration,
        showFinancialCentsIntegration,
        showKeeperIntegration,
        featureFlags,
        texts
    });
    const isReminderTable = tableType === Table.Reminders;
    const columnDefs = useMemo(()=>{
        if (isReminderTable) {
            return [
                {
                    width: 360,
                    mobile: {}
                },
                {
                    width: 220,
                    mobile: {
                        hide: true
                    }
                },
                {
                    width: 220,
                    mobile: {
                        hide: true
                    }
                },
                {
                    width: [
                        STATUS_COLUMN_MIN_WIDTH,
                        STATUS_COLUMN_MAX_WIDTH
                    ],
                    hAlign: 'end',
                    mobile: {
                        hide: true
                    }
                }
            ];
        }
        return [
            {
                width: 360,
                mobile: {}
            },
            {
                width: 280,
                mobile: {
                    hide: true
                }
            },
            {
                width: [
                    STATUS_COLUMN_MIN_WIDTH,
                    STATUS_COLUMN_MAX_WIDTH
                ],
                hAlign: 'end',
                mobile: {
                    hide: true
                }
            }
        ];
    }, [
        isReminderTable
    ]);
    const isClientTab = selectedTab === Tab.Client;
    const recurringIntervalLabel = isClientTab ? texts.tableHeaders.interval() : texts.tableHeaders.delay();
    const headerItems = useMemo(()=>isReminderTable ? [
            {
                value: texts.tableHeaders.delayedAction()
            },
            {
                value: texts.tableHeaders.recipients()
            },
            {
                value: recurringIntervalLabel
            },
            {
                value: texts.tableHeaders.status()
            }
        ] : [
            {
                value: texts.tableHeaders.eventName()
            },
            {
                value: texts.tableHeaders.recipients()
            },
            {
                value: texts.tableHeaders.status()
            }
        ], [
        isReminderTable,
        recurringIntervalLabel,
        texts
    ]);
    return /*#__PURE__*/ _jsx(BiContainer, {
        name: "notification_settings_v2",
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: columnDefs,
            hasActionColumn: isMobileDevice,
            children: [
                /*#__PURE__*/ _jsx(TableList.Header, {
                    items: headerItems,
                    testId: testIds.Root(),
                    className: styles.header
                }),
                Object.entries(notificationsTextMap).map((param)=>{
                    let [key, label] = param;
                    const tabSettings = data[selectedTab];
                    const notification = tabSettings[key];
                    if (!notification || notification.type !== typeMap[tableType]) {
                        return null;
                    }
                    const notificationName = key;
                    const editability = notification.editability;
                    const isAlwaysOn = editability.recipients === false;
                    const isToggleOn = isAlwaysOn || deriveIsActive(notification);
                    const recurringIntervalText = notification.type === NotificationSettingsEntryType.Recurring ? isClientTab ? formatClientReminderInterval(notification.recurring.minutes, notification.recurring.times, texts) : formatDelay(notification.recurring.minutes) : '';
                    const selectedRecipients = new Set(notification.recipientTypes);
                    const orderedRecipientTypes = notification.recipientTypes.includes('all') ? notification.recipientTypes : notification.recipientTypeOptions.filter((type)=>selectedRecipients.has(type));
                    const recipientLabels = getRecipientTypesText({
                        recipientTypes: orderedRecipientTypes,
                        notificationName,
                        texts
                    });
                    const joinedRecipients = recipientLabels.join(', ');
                    const tooltipText = texts.contentCustomization.alwaysOnTooltip();
                    // Always-on entries (recipients=false) cannot be toggled.
                    const isToggleDisabled = isAlwaysOn;
                    const showEditPill = editability.content || editability.recipients;
                    const switchTooltip = isAlwaysOn ? {
                        title: tooltipText
                    } : undefined;
                    const isRowLoading = !!isLoading && pendingNotificationName === notificationName;
                    const showRowLoader = isRowLoading && isMobileDevice;
                    const editLabel = commonTexts.buttons.edit();
                    const onEdit = ()=>onEditClick({
                            notification,
                            notificationName,
                            notificationLabel: label
                        });
                    const editControl = isMobileDevice ? /*#__PURE__*/ _jsx(IconButton, {
                        size: "smallMedium",
                        "aria-label": editLabel,
                        bi: {
                            name: 'edit_notification',
                            data: {
                                notificationName
                            }
                        },
                        testId: testIds.EditPill(notificationName),
                        className: styles.editPill,
                        onClick: onEdit,
                        children: /*#__PURE__*/ _jsx(Edit, {
                            size: 20,
                            testId: testIds.EditIcon(notificationName)
                        })
                    }) : /*#__PURE__*/ _jsx(Button, {
                        size: "small",
                        bi: {
                            name: 'edit_notification',
                            data: {
                                notificationName
                            }
                        },
                        testId: testIds.EditPill(notificationName),
                        className: styles.editPill,
                        onClick: onEdit,
                        children: editLabel
                    });
                    const actionGroup = /*#__PURE__*/ _jsxs("div", {
                        className: styles.statusCell,
                        children: [
                            showEditPill && editControl,
                            /*#__PURE__*/ _jsx(Switch, {
                                checked: isToggleOn,
                                disabled: isToggleDisabled || isRowLoading,
                                tooltip: switchTooltip,
                                bi: {
                                    name: 'notification_enabled_toggle',
                                    data: {
                                        notificationName
                                    }
                                },
                                testId: testIds.Toggle(notificationName),
                                onChange: (_, checked)=>onToggleClick({
                                        notification,
                                        notificationName,
                                        nextStatus: checked ? NotificationStatus.Active : NotificationStatus.Silent
                                    })
                            }),
                            showRowLoader && /*#__PURE__*/ _jsx(CircularLoader, {
                                size: 20,
                                testId: testIds.ToggleLoader(notificationName)
                            })
                        ]
                    });
                    const mobileEventCell = /*#__PURE__*/ _jsxs(Column, {
                        gap: 4,
                        className: styles.mobileEventCell,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                weight: "normal",
                                testId: testIds.EventName(notificationName),
                                children: label
                            }),
                            /*#__PURE__*/ _jsxs(Column, {
                                gap: 0,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        children: "".concat(texts.tableHeaders.recipients(), ":")
                                    }),
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        title: joinedRecipients,
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            skin: "secondary",
                                            className: styles.recipientLabel,
                                            testId: testIds.Recipients(notificationName),
                                            children: joinedRecipients
                                        })
                                    })
                                ]
                            }),
                            isReminderTable && notification.type === NotificationSettingsEntryType.Recurring && /*#__PURE__*/ _jsxs(Column, {
                                gap: 0,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        children: "".concat(recurringIntervalLabel, ":")
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        testId: testIds.Interval(notificationName),
                                        children: recurringIntervalText
                                    })
                                ]
                            })
                        ]
                    });
                    return /*#__PURE__*/ _jsx(TableList.Row, {
                        size: "fluid",
                        className: cn(styles.row, {
                            [styles.rowMobile]: isMobileDevice
                        }),
                        testId: testIds.Row(notificationName),
                        actionColumn: isMobileDevice ? actionGroup : undefined,
                        items: [
                            {
                                value: isMobileDevice ? mobileEventCell : /*#__PURE__*/ _jsx(Typography, {
                                    weight: "normal",
                                    testId: testIds.EventName(notificationName),
                                    children: label
                                })
                            },
                            {
                                value: /*#__PURE__*/ _jsx(Tooltip, {
                                    title: joinedRecipients,
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        className: styles.recipientLabel,
                                        testId: testIds.Recipients(notificationName),
                                        children: joinedRecipients
                                    })
                                })
                            },
                            ...isReminderTable && notification.type === NotificationSettingsEntryType.Recurring ? [
                                {
                                    value: /*#__PURE__*/ _jsx(Typography, {
                                        testId: testIds.Interval(notificationName),
                                        children: recurringIntervalText
                                    })
                                }
                            ] : [],
                            {
                                value: actionGroup
                            }
                        ]
                    }, key);
                })
            ]
        })
    });
}
